import { UserModel } from '@data-layer/models/user';
import { ImporterOfRecordContactInformationModel } from '@data-layer/models/companies';
import { enumDigitalSignState } from '@data-layer/enums/digital-signs/enumDigitalSignState';
import { enumPaymentState } from '@data-layer/enums/payments/enumPaymentState';

/** Subset of the IOR contact compared against the current user (reuses the backend model shape). */
export type IorContactValues = Pick<ImporterOfRecordContactInformationModel, 'emailAddress' | 'phoneNumber'>;

/** Minimal digital-sign shape needed to decide grandfathering. */
export interface IorGrandfatherSign {
  state: enumDigitalSignState;
  paymentState: enumPaymentState;
}

/**
 * #13175/#13212 — stateless matching of the IOR contact against the current user.
 * Shared by company-ior-contact-form (field validation) and edit-company (the POA
 * "Sign digitally now" gate), so the gate is correct even on the read-only company-view
 * page where the IOR form component is not mounted.
 */
export class IorContactMatchHelper {
  public static normalizeText(value: string | null | undefined): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  public static normalizePhone(value: string | null | undefined): string {
    return (value ?? '').toString().replace(/\D/g, '');
  }

  public static emailMatchesUser(ior: IorContactValues | null | undefined, user: UserModel | null | undefined): boolean {
    if (!ior || !user) {
      return false;
    }
    const userEmail: string = IorContactMatchHelper.normalizeText(user.email);
    const iorEmail: string = IorContactMatchHelper.normalizeText(ior.emailAddress);
    return userEmail !== '' && iorEmail !== '' && iorEmail === userEmail;
  }

  public static phoneMatchesUser(ior: IorContactValues | null | undefined, user: UserModel | null | undefined): boolean {
    if (!ior || !user) {
      return false;
    }
    const userPhone: string = IorContactMatchHelper.normalizePhone(user.phoneNumber);
    const iorPhone: string = IorContactMatchHelper.normalizePhone(ior.phoneNumber);
    return userPhone !== '' && iorPhone !== '' && iorPhone === userPhone;
  }

  // #13175 — grandfather existing users whose POA is already signed (Completed) or paid.
  // PENDING counts as paid (funds may be captured) so a user mid-payment isn't blocked.
  public static isPoaGrandfathered(latest: IorGrandfatherSign | null | undefined): boolean {
    return (
      !!latest &&
      (latest.state === enumDigitalSignState.Completed ||
        [enumPaymentState.SUCCESS, enumPaymentState.PENDING].includes(latest.paymentState))
    );
  }
}
